/* ScansPhotosStep — iter97.77 Wave 2
 *
 * Premium accordion-driven upload step for Step 3 of the case wizard.
 * Replaces the legacy flat-grid StepRecords for this step only. Faithful
 * React port of the doctor-supplied HTML mockup (see iter97.77 changelog
 * in PRD.md). Four collapsible accordions:
 *
 *   1. 3D Scans       — embeds scan provenance + planning software export
 *   2. Intraoral      — 3 required + 3 optional photo slots
 *   3. Extraoral      — 2 required + 2 optional photo slots
 *   4. X-rays         — 1 required (OPG) + 2 optional (Ceph, CBCT)
 *
 * Plus:
 *   - Progress bar tracking the 4 sections' required-completion state.
 *   - Confirmation strip (3 chairside QA checkboxes — persists on clinical).
 *   - Upload summary glance box at the bottom.
 *
 * The component is purpose-built for Step 3 (i.e. no excludeGroups /
 * onlyGroups props). It accepts the same `slots / uploads / onUpload /
 * onRemove / clinical / setClinical` props the legacy StepRecords used
 * so the upload pipeline + autosave on the page stays unchanged.
 */
import React, { useMemo, useState, useEffect, useRef } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
    CaretDown,
    CheckCircle,
    Upload,
    X,
    Check,
    Info,
    Cube,
    Camera,
    User as UserIcon,
    Radioactive,
    ArrowCircleUp,
    ArrowCircleDown,
    GitMerge,
    FileZip,
    ArrowRight,
    ArrowLeft,
    Smiley,
    Scan,
    Stack,
    WarningCircle,
    Warning,
    FolderOpen,
    Lightning,
    Tag,
    UploadSimple,
    Calendar,
    Atom,
} from "@phosphor-icons/react";
import ThirdPartyDeliverableSection from "@/components/case/ThirdPartyDeliverableSection";

/* ───────────────────────── Scanner catalogue ─────────────────────────
 * Maps a human-friendly scanner+model label to its enum slug. The slug
 * is what gets persisted as clinical.scan_device; the full label goes to
 * clinical.scan_model (free-text fidelity).
 * Order in the dropdown matches market share for top-of-mind ranking.
 */
const SCANNERS = [
    { label: "iTero Element 5D", slug: "itero" },
    { label: "iTero Element 2", slug: "itero" },
    { label: "3Shape TRIOS 5", slug: "trios" },
    { label: "3Shape TRIOS 4", slug: "trios" },
    { label: "Medit i700", slug: "medit" },
    { label: "Medit i500", slug: "medit" },
    { label: "Planmeca Emerald S", slug: "other" },
    { label: "Cerec Primescan", slug: "primescan" },
    { label: "Other intraoral scanner", slug: "other" },
    { label: "Physical impression → digital pour", slug: "impression" },
];

/* ───────────────── Lookup the slot's metadata by key ─────────────────
 * A small helper so each tile can grab its slot config from the parent's
 * metadata.file_slots array using a single key.
 */
function pickSlot(slots, key) {
    return (slots || []).find((s) => s.key === key) || null;
}

/* ─────────────────── Bulk-upload filename router ─────────────────────
 * iter97.77 Wave 4 — when a doctor selects multiple files at once via
 * the "Select multiple" button, route each file to the best-matching
 * slot key in the section using filename heuristics. Patterns are
 * ordered MOST-SPECIFIC first so e.g. "upper_occlusal.jpg" matches
 * `intraoral_upper_occlusal` before falling through to the generic
 * "upper" pattern (which would otherwise grab it for `scan_upper`).
 *
 * Returns an object: { routes: [{ slotKey, file }], unmatched: [files] }
 *
 * Each slot is consumed at most once per batch — if two "front" files
 * arrive, only the first wires up and the second goes to `unmatched`.
 */
const SECTION_ROUTES = {
    scans: [
        { keys: ["upper", "maxillary"], slotKey: "scan_upper" },
        { keys: ["lower", "mandibular", "mand"], slotKey: "scan_lower" },
        { keys: ["bite", "occlusion", "occlusal"], slotKey: "scan_bite" },
    ],
    intra: [
        // More specific patterns first.
        { keys: ["upper_occlusal", "upper-occlusal", "maxillary_occlusal"], slotKey: "intraoral_upper_occlusal" },
        { keys: ["lower_occlusal", "lower-occlusal", "mandibular_occlusal"], slotKey: "intraoral_lower_occlusal" },
        { keys: ["anterior_rest", "front_rest", "at_rest", "lips_apart"], slotKey: "intraoral_anterior_rest" },
        { keys: ["right_buccal", "buccal_right", "right_lateral", "right"], slotKey: "intraoral_right" },
        { keys: ["left_buccal", "buccal_left", "left_lateral", "left"], slotKey: "intraoral_left" },
        { keys: ["front", "frontal", "anterior", "bite"], slotKey: "intraoral_front" },
    ],
    extra: [
        { keys: ["smile", "smile_view", "retracted"], slotKey: "extraoral_smile" },
        { keys: ["right_profile", "profile_right", "right"], slotKey: "extraoral_profile_right" },
        { keys: ["left_profile", "profile_left", "profile", "lateral"], slotKey: "extraoral_profile" },
        { keys: ["front", "frontal", "face", "full_face"], slotKey: "extraoral_front" },
    ],
    xray: [
        { keys: ["cbct", "cone_beam", "dicom"], slotKey: "cbct" },
        { keys: ["ceph", "cephalogram", "lateral"], slotKey: "xray_lateral_ceph" },
        { keys: ["opg", "panoramic", "pano", "orthopantomogram"], slotKey: "xray_opg" },
    ],
};

function routeFilesToSlots(files, section, alreadyUploaded) {
    const routes = [];
    const unmatched = [];
    const consumed = new Set(alreadyUploaded || []);
    const rules = SECTION_ROUTES[section] || [];
    for (const file of files) {
        // Normalise filename — lowercased, separators collapsed to "_"
        // so "Upper Occlusal.JPG" and "upper-occlusal.jpg" both match.
        const norm = file.name.toLowerCase().replace(/[\s-]+/g, "_");
        let matched = null;
        for (const rule of rules) {
            if (consumed.has(rule.slotKey)) continue;
            if (rule.keys.some((k) => norm.includes(k))) {
                matched = rule.slotKey;
                break;
            }
        }
        if (matched) {
            routes.push({ slotKey: matched, file });
            consumed.add(matched);
        } else {
            unmatched.push(file);
        }
    }
    return { routes, unmatched };
}

/* ─────────────────────── Bulk-upload button ──────────────────────────
 * Sits at the top of each accordion body. Lets the doctor pick many
 * files at once; the router above maps each filename → slot. Useful
 * when a clinic exports all photos as `front.jpg / left.jpg / ...`
 * from their camera and dumps the folder in one go.
 */
function BulkUploadButton({ section, onUpload, uploadsState, slotsKnown, testid }) {
    const inputId = `bulk-upload-${section}`;
    const handleFiles = (files) => {
        const arr = Array.from(files || []);
        if (arr.length === 0) return;
        // Don't try to claim slots that are already uploaded.
        const alreadyUploaded = slotsKnown.filter((k) => uploadsState?.[k]?.uploaded);
        const { routes, unmatched } = routeFilesToSlots(arr, section, alreadyUploaded);
        routes.forEach(({ slotKey, file }) => onUpload(slotKey, file));
        if (routes.length > 0) {
            toast.success(`${routes.length} file${routes.length === 1 ? "" : "s"} auto-routed`);
        }
        if (unmatched.length > 0) {
            toast.message(
                `${unmatched.length} file${unmatched.length === 1 ? "" : "s"} couldn't be auto-matched`,
                { description: `Drag them onto a specific tile, or rename to match (e.g. "upper.stl", "left_buccal.jpg")` }
            );
        }
    };
    return (
        <div className="flex items-center justify-end mb-2.5" data-testid={testid}>
            <input
                id={inputId}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
            />
            <button
                type="button"
                onClick={() => document.getElementById(inputId)?.click()}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-primary px-2.5 py-1 rounded-full border-[0.5px] border-primary/30 bg-primary/5 hover:bg-primary/10 hover:border-primary transition-colors"
                data-testid={`${testid}-button`}
                title="Pick multiple files at once — we'll auto-match them to slots by filename"
            >
                <Lightning size={11} weight="fill" />
                Bulk upload
            </button>
        </div>
    );
}

/* ─────────────────────────── UploadTile ──────────────────────────────
 * Custom upload-zone matching the mockup's `.uz` style. Replaces the
 * legacy SlotUploader for this step only. Shows:
 *   - Required / Optional / Uploaded badge in the top-right
 *   - Centered icon (passes from caller — e.g. ArrowCircleUp for upper arch)
 *   - Two-line label (title + format hint)
 *   - On-uploaded state: success border + emerald icon + "✓" suffix on title
 *   - Click anywhere on the tile to open the file picker
 *   - Replace + Remove controls appear on hover/focus once uploaded
 */
function UploadTile({ slot, state, onPick, onRemove, icon, title, hint, required = false, testid }) {
    const uploaded = state?.uploaded;
    const uploading = state?.uploading;
    const removing = state?.removing;
    const inputId = `uz-${slot.key}`;
    /* iter97.77 Wave 4 — drag-and-drop. Track per-tile drag-over state
       so we can paint a clear "drop here" affordance. The onDrop hook
       simply forwards the first file to the existing onPick pipeline
       (same flow as click-to-pick). */
    const [dragOver, setDragOver] = useState(false);
    const badge = uploading
        ? { cls: "uz-pending", text: state?.progress != null ? `Uploading ${state.progress}%` : "Uploading…" }
        : uploaded
            ? { cls: "uz-done", text: "Uploaded" }
            : required
                ? { cls: "uz-req", text: "Required" }
                : { cls: "uz-opt", text: "Optional" };
    return (
        <div
            className={
                /* iter98.99ag — Beefier tile body. The old px-2.5 py-3
                   shoebox tile felt sparse and uninviting; doctors
                   missed that it was even a click-target. New layout:
                   chunkier padding, bigger icon avatar, hover lift,
                   tinted backgrounds for the required vs optional vs
                   uploaded states. */
                "group relative rounded-xl px-3.5 pt-3 pb-3.5 transition-all cursor-pointer overflow-hidden " +
                (dragOver
                    ? "bg-primary/10 border-2 border-dashed border-primary shadow-md scale-[1.02]"
                    : uploaded
                        ? "bg-emerald-50/60 border border-emerald-300 hover:border-emerald-400 hover:shadow-sm"
                        : required
                            ? "border border-dashed border-rose-300/80 bg-rose-50/30 hover:bg-rose-50/60 hover:border-rose-400 hover:shadow-sm hover:-translate-y-0.5"
                            : "border border-dashed border-border bg-secondary/30 hover:bg-secondary/60 hover:border-foreground/30 hover:shadow-sm hover:-translate-y-0.5")
            }
            data-testid={testid || `case-wizard-slot-${slot.key}`}
            onClick={() => document.getElementById(inputId)?.click()}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
            onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragOver(false);
                const f = e.dataTransfer?.files?.[0];
                if (f) onPick(f);
            }}
        >
            <input
                id={inputId}
                type="file"
                accept={slot.accepts}
                className="hidden"
                onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onPick(f);
                    e.target.value = "";
                }}
            />
            <span
                className={
                    "absolute top-2 right-2 text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded-full " +
                    (badge.cls === "uz-done"
                        ? "bg-emerald-600 text-white shadow-sm"
                        : badge.cls === "uz-pending"
                            ? "bg-sky-600 text-white shadow-sm"
                            : badge.cls === "uz-req"
                                ? "bg-rose-600 text-white shadow-sm"
                                : "bg-secondary text-muted-foreground border-[0.5px] border-border")
                }
            >
                {badge.text}
            </span>
            {/* Icon avatar — bigger + tinted so the tile reads as an
                actionable card, not just a hint dropzone. */}
            <div
                className={
                    "mx-auto w-11 h-11 rounded-xl grid place-items-center mb-2 transition-colors " +
                    (uploaded
                        ? "bg-emerald-600 text-white"
                        : required
                            ? "bg-rose-100 text-rose-700 group-hover:bg-rose-200"
                            : "bg-primary/10 text-primary group-hover:bg-primary/15")
                }
            >
                {uploaded ? <CheckCircle size={22} weight="fill" /> : icon}
            </div>
            <div className={`text-center text-[13px] font-semibold leading-tight ${uploaded ? "text-emerald-900" : "text-foreground"}`}>
                {title}
            </div>
            <div className="text-center text-[10.5px] text-muted-foreground mt-1 leading-snug">
                {removing
                    ? "Removing…"
                    : uploading
                        ? (
                            <span className="inline-flex items-center gap-1 text-primary font-semibold">
                                <UploadSimple size={10} weight="bold" />
                                {state?.progress != null ? `${state.progress}% uploaded` : "Uploading…"}
                            </span>
                        )
                        : uploaded
                            ? <span className="truncate inline-block max-w-full">{state?.name || "File uploaded"}</span>
                            : (
                                <span className="inline-flex items-center gap-1 text-foreground/55">
                                    <UploadSimple size={10} weight="bold" />
                                    {hint || "Click or drag to upload"}
                                </span>
                            )}
            </div>
            {/* iter101.16 — Live upload progress bar. Sits above the
                hover-only Replace/Remove chip strip so the doctor sees
                real byte-level movement, not just a spinning label. */}
            {uploading && (
                <div
                    className="mt-2 h-1.5 rounded-full bg-sky-100 overflow-hidden"
                    data-testid={`case-wizard-slot-${slot.key}-progress`}
                >
                    <div
                        className="h-full bg-sky-600 rounded-full transition-[width] duration-200"
                        style={{ width: `${state?.progress ?? 5}%` }}
                    />
                </div>
            )}
            {uploaded && !uploading && (
                <div className="absolute bottom-1.5 right-1.5 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                    <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); document.getElementById(inputId)?.click(); }}
                        className="text-[9px] font-semibold text-primary px-2 py-0.5 rounded-full bg-white shadow-sm hover:bg-primary/10 border border-border"
                        data-testid={`case-wizard-slot-${slot.key}-replace`}
                    >
                        Replace
                    </button>
                    <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); onRemove(); }}
                        disabled={removing}
                        className="w-5 h-5 rounded-full grid place-items-center bg-white shadow-sm text-muted-foreground hover:bg-rose-50 hover:text-rose-700 border border-border"
                        title="Remove file"
                        data-testid={`case-wizard-slot-${slot.key}-remove`}
                    >
                        <X size={10} weight="bold" />
                    </button>
                </div>
            )}
        </div>
    );
}

/* ───────────────────────── Accordion shell ──────────────────────────
 * Self-contained collapsible card. Shows an icon avatar, title + sub,
 * a `n / total` count pill that turns emerald once all required items
 * are completed, and a chevron that rotates on toggle.
 */
function AccordionCard({ id, icon, iconTone, title, sub, count, total, allRequiredDone, open, onToggle, children, testid }) {
    /* iter98.99t — Aligned to the same shell as <ExamAccordion> (Step 2)
       and <LabSection> (Step 5) so the wizard reads as one consistent
       accordion stack across steps:
         · group + relative + hover:shadow-sm + hover:border-foreground/15
         · header text-[13.5px] font-semibold
         · 8x8 rounded-[9px] icon tile
         · open header divider via border-b-[0.5px]
         · CaretDown 13px (was 16) */
    const toneCls = {
        teal: "bg-emerald-100 text-emerald-700",
        blue: "bg-sky-100 text-sky-700",
        amber: "bg-amber-100 text-amber-700",
        purple: "bg-indigo-100 text-indigo-700",
    }[iconTone] || "bg-emerald-100 text-emerald-700";
    return (
        <section
            className="group relative bg-card border-[0.5px] border-border rounded-[12px] overflow-hidden transition-all duration-150 hover:shadow-sm hover:border-foreground/15 mb-2.5"
            data-testid={testid}
            data-open={open ? "true" : "false"}
            data-complete={allRequiredDone ? "true" : "false"}
        >
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3 cursor-pointer select-none text-left hover:bg-muted/30 transition-colors ${open ? "border-b-[0.5px] border-border" : ""}`}
                data-testid={`${testid}-toggle`}
            >
                <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-8 h-8 rounded-[9px] flex items-center justify-center shrink-0 ${toneCls}`}>
                        {icon}
                    </div>
                    <div className="min-w-0">
                        <div className="text-[13.5px] font-semibold leading-tight truncate">{title}</div>
                        {sub && <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight truncate">{sub}</div>}
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {allRequiredDone ? (
                        <span
                            className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800"
                            data-testid={`${testid}-count`}
                        >
                            <CheckCircle size={11} weight="fill" /> Complete
                        </span>
                    ) : (
                        <span
                            className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full ${count === 0 ? "bg-muted text-muted-foreground" : "bg-amber-100 text-amber-900"}`}
                            data-testid={`${testid}-count`}
                        >
                            {count} / {total} {count === 0 ? "uploaded" : "uploaded"}
                        </span>
                    )}
                    <CaretDown size={13} className={`text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
                </div>
            </button>
            {open && (
                <div className="px-4 py-3.5">
                    {children}
                </div>
            )}
        </section>
    );
}

/* ─────────────────── Scan freshness pill ──────────────────────────────
 * Reads clinical.scan_date and emits one of three colored pills:
 *  • emerald  → ≤ 90 d (excellent)
 *  • amber    → ≤ 180 d (acceptable)
 *  • rose     → > 180 d (consider rescanning)
 */
function ScanFreshnessPill({ dateStr }) {
    if (!dateStr) return null;
    const scanDate = new Date(dateStr);
    if (isNaN(scanDate.getTime())) return null;
    const diffDays = Math.floor((Date.now() - scanDate.getTime()) / 86400000);
    if (diffDays < 0) return null; // future date — skip
    const tone = diffDays <= 90
        ? { bg: "bg-emerald-100", text: "text-emerald-700", icon: <CheckCircle size={11} weight="fill" />, msg: "excellent" }
        : diffDays <= 180
            ? { bg: "bg-amber-100", text: "text-amber-700", icon: <WarningCircle size={11} weight="fill" />, msg: "acceptable" }
            : { bg: "bg-rose-100", text: "text-rose-700", icon: <Warning size={11} weight="fill" />, msg: "consider rescanning" };
    return (
        <div className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full mt-1.5 ${tone.bg} ${tone.text}`} data-testid="scan-freshness-pill">
            {tone.icon}
            <span>Taken {diffDays} day{diffDays === 1 ? "" : "s"} ago — {tone.msg}</span>
        </div>
    );
}

/* ────────────────── Planning software pill row ────────────────────── */
const SW_LIST = [
    { name: "ClinCheck", dot: "#E74C3C" },
    { name: "3Shape", dot: "#2C3E8C" },
    { name: "Maestro 3D", dot: "#F5A623" },
    { name: "OnyxCeph", dot: "#27AE60" },
    { name: "exocad", dot: "#8E44AD" },
    { name: "Other", dot: "#6B7280" },
];

function SoftwarePillRow({ value, onChange }) {
    return (
        <div className="flex flex-wrap gap-1.5">
            {SW_LIST.map((sw) => {
                const sel = value === sw.name;
                return (
                    <button
                        key={sw.name}
                        type="button"
                        onClick={() => onChange(sw.name)}
                        data-testid={`sw-pill-${sw.name.replace(/\s+/g, "-").toLowerCase()}`}
                        aria-pressed={sel}
                        className={
                            "inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border-[0.5px] transition-colors " +
                            (sel
                                ? "border-primary bg-primary/10 text-primary font-semibold"
                                : "border-border bg-card text-muted-foreground hover:border-foreground/40")
                        }
                    >
                        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: sw.dot }} />
                        {sw.name}
                    </button>
                );
            })}
        </div>
    );
}

/* ───────────────────────── Main component ─────────────────────────── */
export default function ScansPhotosStep({
    slots,
    uploads,
    onUpload,
    onRemove,
    clinical,
    setClinical,
    planningMode = "coe",
    /* iter98.99q — Self-Plan only: 3rd-Party Deliverable state lives on
       `planTimeline` (mirrored to case.plan via /cases/{id}/doctor-plan).
       Section renders at the very bottom for Self-Plan; hidden for CoE
       (the planner authors the deliverable on their side). */
    planTimeline = null,
    setPlanTimeline = null,
    caseId = "",
}) {
    const isSelfPlan = planningMode === "doctor";

    /* Resolve every slot we render by key so the call sites stay declarative. */
    const S = useMemo(() => ({
        scan_upper: pickSlot(slots, "scan_upper"),
        scan_lower: pickSlot(slots, "scan_lower"),
        scan_bite: pickSlot(slots, "scan_bite"),
        planning: pickSlot(slots, "planning_software_export"),
        intraoral_front: pickSlot(slots, "intraoral_front"),
        intraoral_right: pickSlot(slots, "intraoral_right"),
        intraoral_left: pickSlot(slots, "intraoral_left"),
        intraoral_anterior_rest: pickSlot(slots, "intraoral_anterior_rest"),
        intraoral_upper_occlusal: pickSlot(slots, "intraoral_upper_occlusal"),
        intraoral_lower_occlusal: pickSlot(slots, "intraoral_lower_occlusal"),
        extraoral_front: pickSlot(slots, "extraoral_front"),
        extraoral_profile: pickSlot(slots, "extraoral_profile"),
        extraoral_profile_right: pickSlot(slots, "extraoral_profile_right"),
        extraoral_smile: pickSlot(slots, "extraoral_smile"),
        xray_opg: pickSlot(slots, "xray_opg"),
        xray_lateral_ceph: pickSlot(slots, "xray_lateral_ceph"),
        cbct: pickSlot(slots, "cbct"),
    }), [slots]);

    /* Uploaded-state helper for any given slot. */
    const ok = (key) => !!uploads?.[key]?.uploaded;

    /* Per-section uploaded counts + total visible. */
    const scansCount = (ok("scan_upper") ? 1 : 0) + (ok("scan_lower") ? 1 : 0) + (ok("scan_bite") ? 1 : 0);
    const intraCount = (ok("intraoral_front") ? 1 : 0) + (ok("intraoral_right") ? 1 : 0) + (ok("intraoral_left") ? 1 : 0)
        + (ok("intraoral_anterior_rest") ? 1 : 0) + (ok("intraoral_upper_occlusal") ? 1 : 0) + (ok("intraoral_lower_occlusal") ? 1 : 0);
    const extraCount = (ok("extraoral_front") ? 1 : 0) + (ok("extraoral_profile") ? 1 : 0)
        + (ok("extraoral_profile_right") ? 1 : 0) + (ok("extraoral_smile") ? 1 : 0);
    const xrayCount = (ok("xray_opg") ? 1 : 0) + (ok("xray_lateral_ceph") ? 1 : 0) + (ok("cbct") ? 1 : 0);

    /* Required-completion state per section (drives the count pill color
       and the top progress bar). */
    const scansRequiredOk = ok("scan_upper") && ok("scan_lower");
    const intraRequiredOk = ok("intraoral_front") && ok("intraoral_right") && ok("intraoral_left");
    const extraRequiredOk = ok("extraoral_front") && ok("extraoral_profile");
    const xrayRequiredOk = ok("xray_opg");
    const sectionsDone = [scansRequiredOk, intraRequiredOk, extraRequiredOk, xrayRequiredOk].filter(Boolean).length;
    const progressPct = Math.round((sectionsDone / 4) * 100);

    /* iter98.99s — Deliverable accordion (5th item, Self-Plan only).
       Tracks whether the doctor has filled the required Planning Software
       + Plan Viewer Link fields. The 4 above are upload-counted; this
       one is value-counted. */
    const deliverableDone = !!(planTimeline && (planTimeline.planning_software || "").trim() && (planTimeline.external_link || "").trim());
    const deliverableCount = deliverableDone ? 1 : 0;

    /* iter98.99s — Accordion open state. User direction: "by default
       make all accordions in all steps stay collapsed". All sections
       start CLOSED; the doctor opens what they want to upload next.
       Auto-collapse on completion still runs to advance the rhythm
       once a section is done. */
    const initialOpen = { scans: false, intra: false, extra: false, xray: false, deliverable: false };
    const [open, setOpen] = useState(initialOpen);
    const toggle = (k) => {
        // Manual toggle — also marks the section as "user-controlled" so
        // the auto-collapse effect won't re-flip it. We reset that flag
        // each session by storing it in a ref (not state) so manual
        // overrides persist for the current visit only.
        userOverride.current[k] = true;
        setOpen((p) => ({ ...p, [k]: !p[k] }));
    };

    /* iter97.77 Wave 3 — auto-collapse / auto-expand cadence.
       Watches the 4 required-completion booleans. When a section
       transitions from incomplete → complete, we collapse it and open
       the next incomplete one (mirrors Apple's setup-wizard rhythm).
       Manual toggles always win — we track which sections the user has
       explicitly opened/closed via `userOverride.current` and skip the
       auto-collapse for those. */
    const userOverride = useRef({});
    const prevDone = useRef({ scans: scansRequiredOk, intra: intraRequiredOk, extra: extraRequiredOk, xray: xrayRequiredOk });
    useEffect(() => {
        const order = ["scans", "intra", "extra", "xray"];
        const done = { scans: scansRequiredOk, intra: intraRequiredOk, extra: extraRequiredOk, xray: xrayRequiredOk };
        // Find a section that transitioned from incomplete → complete
        const transitionedKey = order.find((k) => done[k] && !prevDone.current[k]);
        prevDone.current = done;
        if (!transitionedKey) return;
        // Don't auto-collapse if the user explicitly opened/closed this
        // section in the current visit.
        if (userOverride.current[transitionedKey]) return;
        // Don't auto-collapse if ALL sections are now complete (avoid the
        // jarring "everything closed" state at the end).
        const allComplete = Object.values(done).every(Boolean);
        if (allComplete) return;
        // Find the next incomplete section to surface.
        const nextKey = order.find((k) => !done[k] && !userOverride.current[k]);
        setOpen((p) => ({
            ...p,
            [transitionedKey]: false,
            ...(nextKey ? { [nextKey]: true } : {}),
        }));
    }, [scansRequiredOk, intraRequiredOk, extraRequiredOk, xrayRequiredOk]);

    /* Clinical state mutators — used by scan provenance + confirmation
       strip. Falls back to local-only state if the parent didn't pass a
       setClinical (e.g. preview mode), but in the wizard we always have
       both. */
    const updClinical = (k, v) => {
        if (typeof setClinical === "function") {
            setClinical((c) => ({ ...c, [k]: v }));
        }
    };

    /* Scanner dropdown handler — single dropdown that sets BOTH the
       scan_device enum slug AND the human-readable scan_model in one
       click. See SCANNERS map for the mapping. */
    const onScannerChange = (label) => {
        const hit = SCANNERS.find((s) => s.label === label);
        updClinical("scan_device", hit ? hit.slug : "");
        updClinical("scan_model", label);
    };
    const scannerLabel = clinical?.scan_model || "";

    /* Detected-software label (shown as the green badge when an export
       file has been uploaded). Sticky state — once detected, persists
       on rx so future renders don't lose it. */
    const planningSoftware = clinical?.planning_software || "";

    /* ── Render ── */
    return (
        <div className="space-y-4 pb-[40vh]" data-testid="case-wizard-scans-photos-v2">
            {/* iter98.99m — Intro paragraph removed. The universal
                `WizardStepHero` rendered above already covers
                "Upload patient records — STL scans, intra-oral and
                extra-oral photos." Restating it here was redundant. */}

            {/* iter101.87 — Section-level "Upload progress" bar
                removed per user direction. Each accordion header
                already shows its own "X of N" count + green tick
                when required uploads land, so the aggregate bar
                was duplicative. */}

            {/* ── ACCORDION 1 — 3D Scans ── */}
            <AccordionCard
                testid="acc-scans"
                icon={<Cube size={16} weight="duotone" />}
                iconTone="teal"
                title="3D Scans"
                sub="STL · PLY · OBJ · ZIP export from planning software"
                count={scansCount}
                total={3}
                allRequiredDone={scansRequiredOk}
                open={!!open.scans}
                onToggle={() => toggle("scans")}
            >
                {/* iter97.77 Wave 4 — bulk upload (filename-routed) sits
                    at the top of the accordion body so the doctor sees
                    it as the fastest path before scrolling through tiles. */}
                <BulkUploadButton
                    section="scans"
                    onUpload={onUpload}
                    uploadsState={uploads}
                    slotsKnown={["scan_upper", "scan_lower", "scan_bite"]}
                    testid="bulk-scans"
                />
                {/* iter98.99ag — Scan provenance redesigned. Was a plain
                    eyebrow + bare select/input row; now a tinted card
                    container with an icon header so doctors immediately
                    grasp "this is record provenance, not another upload
                    tile". Helps lab QA + audit trail accuracy. */}
                <div
                    className="mt-3.5 mb-3 rounded-xl border border-border bg-secondary/40 px-5 py-5"
                    data-testid="scan-provenance-card"
                >
                    <div className="flex items-center gap-2.5 mb-4">
                        <div className="shrink-0 w-8 h-8 rounded-lg bg-primary/10 text-primary grid place-items-center">
                            <Atom size={15} weight="duotone" />
                        </div>
                        <div>
                            <div className="text-[13px] font-semibold text-foreground leading-tight">
                                Scan provenance
                            </div>
                            <p className="text-[11px] text-muted-foreground leading-snug mt-1">
                                Tell us which scanner and when — helps QA flag stale records before they hit the lab floor.
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                        <div>
                            <Label className="text-[10px] font-bold tracking-[0.12em] uppercase text-muted-foreground mb-2 flex items-center gap-1.5">
                                <Scan size={11} weight="bold" /> Scanning device
                            </Label>
                            <select
                                value={scannerLabel}
                                onChange={(e) => onScannerChange(e.target.value)}
                                className="w-full h-10 text-[13px] rounded-lg border border-border bg-card px-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-shadow"
                                data-testid="scanner-device-select"
                            >
                                <option value="">Select scanner…</option>
                                {SCANNERS.map((s) => (
                                    <option key={s.label} value={s.label}>{s.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <Label className="text-[10px] font-bold tracking-[0.12em] uppercase text-muted-foreground mb-2 flex items-center gap-1.5">
                                <Calendar size={11} weight="bold" /> Scan date
                            </Label>
                            <Input
                                type="date"
                                value={clinical?.scan_date || ""}
                                onChange={(e) => updClinical("scan_date", e.target.value)}
                                className="h-10 text-[13px] rounded-lg"
                                data-testid="scan-date-input"
                            />
                            <ScanFreshnessPill dateStr={clinical?.scan_date || ""} />
                        </div>
                    </div>
                </div>

                {/* Arch scans */}
                <div>
                    <div className="flex items-center justify-between gap-2 mb-2.5">
                        <div className="flex items-center gap-2">
                            <span className="text-[11px] font-bold tracking-[0.14em] uppercase text-foreground">
                                Arch scans
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                                Click any tile or drag a file onto it.
                            </span>
                        </div>
                        <span className="hidden sm:inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                            <UploadSimple size={11} weight="bold" /> Drag &amp; drop supported
                        </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {S.scan_upper && (
                            <UploadTile
                                slot={S.scan_upper}
                                state={uploads?.[S.scan_upper.key]}
                                onPick={(f) => onUpload(S.scan_upper.key, f)}
                                onRemove={() => onRemove(S.scan_upper.key)}
                                icon={<ArrowCircleUp size={22} weight="duotone" />}
                                title="Upper arch"
                                hint="STL · PLY · OBJ"
                                required
                            />
                        )}
                        {S.scan_lower && (
                            <UploadTile
                                slot={S.scan_lower}
                                state={uploads?.[S.scan_lower.key]}
                                onPick={(f) => onUpload(S.scan_lower.key, f)}
                                onRemove={() => onRemove(S.scan_lower.key)}
                                icon={<ArrowCircleDown size={22} weight="duotone" />}
                                title="Lower arch"
                                hint="STL · PLY · OBJ"
                                required
                            />
                        )}
                    </div>
                    {S.scan_bite && (
                        <div className="grid grid-cols-1 gap-2.5 mt-2.5">
                            <UploadTile
                                slot={S.scan_bite}
                                state={uploads?.[S.scan_bite.key]}
                                onPick={(f) => onUpload(S.scan_bite.key, f)}
                                onRemove={() => onRemove(S.scan_bite.key)}
                                icon={<GitMerge size={22} weight="duotone" />}
                                title="Bite registration"
                                hint="STL · PLY · OBJ — helps with occlusal accuracy"
                            />
                        </div>
                    )}
                </div>

                {/* iter98.99q — Legacy "Planning software export" sub-card
                    removed. Doctors now declare planning-software lineage
                    via the new <ThirdPartyDeliverableSection> rendered at
                    the bottom of this step (which captures the SAME
                    metadata — planning_software + version — plus the live
                    viewer link). Keeping the upload tile here as well
                    would have caused two competing sources of truth for
                    "what tool authored this plan". */}
            </AccordionCard>

            {/* ── ACCORDION 2 — Intraoral photos ── */}
            <AccordionCard
                testid="acc-intra"
                icon={<Camera size={16} weight="duotone" />}
                iconTone="blue"
                title="Intraoral Photos"
                sub="JPG · PNG · HEIC · min 1MP"
                count={intraCount}
                total={6}
                allRequiredDone={intraRequiredOk}
                open={!!open.intra}
                onToggle={() => toggle("intra")}
            >
                <BulkUploadButton
                    section="intra"
                    onUpload={onUpload}
                    uploadsState={uploads}
                    slotsKnown={["intraoral_front", "intraoral_right", "intraoral_left", "intraoral_anterior_rest", "intraoral_upper_occlusal", "intraoral_lower_occlusal"]}
                    testid="bulk-intra"
                />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
                    {S.intraoral_front && (
                        <UploadTile
                            slot={S.intraoral_front}
                            state={uploads?.[S.intraoral_front.key]}
                            onPick={(f) => onUpload(S.intraoral_front.key, f)}
                            onRemove={() => onRemove(S.intraoral_front.key)}
                            icon={<Smiley size={18} weight="duotone" />}
                            title="Frontal occlusion"
                            hint="Teeth in occlusion"
                            required
                        />
                    )}
                    {S.intraoral_right && (
                        <UploadTile
                            slot={S.intraoral_right}
                            state={uploads?.[S.intraoral_right.key]}
                            onPick={(f) => onUpload(S.intraoral_right.key, f)}
                            onRemove={() => onRemove(S.intraoral_right.key)}
                            icon={<ArrowRight size={18} weight="duotone" />}
                            title="Right buccal"
                            hint="Lateral view"
                            required
                        />
                    )}
                    {S.intraoral_left && (
                        <UploadTile
                            slot={S.intraoral_left}
                            state={uploads?.[S.intraoral_left.key]}
                            onPick={(f) => onUpload(S.intraoral_left.key, f)}
                            onRemove={() => onRemove(S.intraoral_left.key)}
                            icon={<ArrowLeft size={18} weight="duotone" />}
                            title="Left buccal"
                            hint="Lateral view"
                            required
                        />
                    )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                    {S.intraoral_anterior_rest && (
                        <UploadTile
                            slot={S.intraoral_anterior_rest}
                            state={uploads?.[S.intraoral_anterior_rest.key]}
                            onPick={(f) => onUpload(S.intraoral_anterior_rest.key, f)}
                            onRemove={() => onRemove(S.intraoral_anterior_rest.key)}
                            icon={<Smiley size={18} weight="regular" />}
                            title="Front at rest"
                            hint="Lips apart"
                        />
                    )}
                    {S.intraoral_upper_occlusal && (
                        <UploadTile
                            slot={S.intraoral_upper_occlusal}
                            state={uploads?.[S.intraoral_upper_occlusal.key]}
                            onPick={(f) => onUpload(S.intraoral_upper_occlusal.key, f)}
                            onRemove={() => onRemove(S.intraoral_upper_occlusal.key)}
                            icon={<ArrowCircleUp size={18} weight="duotone" />}
                            title="Upper occlusal"
                            hint="Mirror view"
                        />
                    )}
                    {S.intraoral_lower_occlusal && (
                        <UploadTile
                            slot={S.intraoral_lower_occlusal}
                            state={uploads?.[S.intraoral_lower_occlusal.key]}
                            onPick={(f) => onUpload(S.intraoral_lower_occlusal.key, f)}
                            onRemove={() => onRemove(S.intraoral_lower_occlusal.key)}
                            icon={<ArrowCircleDown size={18} weight="duotone" />}
                            title="Lower occlusal"
                            hint="Mirror view"
                        />
                    )}
                </div>
            </AccordionCard>

            {/* ── ACCORDION 3 — Extraoral photos ── */}
            <AccordionCard
                testid="acc-extra"
                icon={<UserIcon size={16} weight="duotone" />}
                iconTone="amber"
                title="Extraoral Photos"
                sub="Full face · profile · smile · JPG · PNG"
                count={extraCount}
                total={4}
                allRequiredDone={extraRequiredOk}
                open={!!open.extra}
                onToggle={() => toggle("extra")}
            >
                <BulkUploadButton
                    section="extra"
                    onUpload={onUpload}
                    uploadsState={uploads}
                    slotsKnown={["extraoral_front", "extraoral_profile", "extraoral_profile_right", "extraoral_smile"]}
                    testid="bulk-extra"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                    {S.extraoral_front && (
                        <UploadTile
                            slot={S.extraoral_front}
                            state={uploads?.[S.extraoral_front.key]}
                            onPick={(f) => onUpload(S.extraoral_front.key, f)}
                            onRemove={() => onRemove(S.extraoral_front.key)}
                            icon={<UserIcon size={18} weight="duotone" />}
                            title="Full face frontal"
                            hint="Neutral expression"
                            required
                        />
                    )}
                    {S.extraoral_profile && (
                        <UploadTile
                            slot={S.extraoral_profile}
                            state={uploads?.[S.extraoral_profile.key]}
                            onPick={(f) => onUpload(S.extraoral_profile.key, f)}
                            onRemove={() => onRemove(S.extraoral_profile.key)}
                            icon={<UserIcon size={18} weight="duotone" />}
                            title="Left profile"
                            hint="90° lateral"
                            required
                        />
                    )}
                    {S.extraoral_profile_right && (
                        <UploadTile
                            slot={S.extraoral_profile_right}
                            state={uploads?.[S.extraoral_profile_right.key]}
                            onPick={(f) => onUpload(S.extraoral_profile_right.key, f)}
                            onRemove={() => onRemove(S.extraoral_profile_right.key)}
                            icon={<UserIcon size={18} weight="regular" />}
                            title="Right profile"
                            hint="90° lateral"
                        />
                    )}
                    {S.extraoral_smile && (
                        <UploadTile
                            slot={S.extraoral_smile}
                            state={uploads?.[S.extraoral_smile.key]}
                            onPick={(f) => onUpload(S.extraoral_smile.key, f)}
                            onRemove={() => onRemove(S.extraoral_smile.key)}
                            icon={<Smiley size={18} weight="duotone" />}
                            title="Smile / retracted"
                            hint="Natural smile"
                        />
                    )}
                </div>
            </AccordionCard>

            {/* ── ACCORDION 4 — X-rays ── */}
            <AccordionCard
                testid="acc-xray"
                icon={<Radioactive size={16} weight="duotone" />}
                iconTone="purple"
                title="X-rays"
                sub="OPG · Ceph · CBCT · JPG · PNG · DICOM"
                count={xrayCount}
                total={3}
                allRequiredDone={xrayRequiredOk}
                open={!!open.xray}
                onToggle={() => toggle("xray")}
            >
                <BulkUploadButton
                    section="xray"
                    onUpload={onUpload}
                    uploadsState={uploads}
                    slotsKnown={["xray_opg", "xray_lateral_ceph", "cbct"]}
                    testid="bulk-xray"
                />
                {/* iter97.77 Wave 4 — CBCT reminder. If the doctor ticked
                    cbct_available=true back in Step 2 but no CBCT file is
                    uploaded here yet, surface an amber prompt right above
                    the tile grid. Catches the most common missing-file
                    case before the doctor leaves the step. */}
                {clinical?.cbct_available && !ok("cbct") && (
                    <div
                        className="flex items-start gap-2 mt-1 mb-3 p-2.5 rounded-[8px] bg-amber-50 border-[0.5px] border-amber-300 animate-pulse"
                        data-testid="cbct-missing-reminder"
                    >
                        <Warning size={14} weight="fill" className="text-amber-700 shrink-0 mt-0.5" />
                        <p className="text-[11.5px] text-amber-800 leading-snug">
                            You flagged a CBCT as available in Step 2 — don&apos;t forget to attach it below before submitting the case.
                        </p>
                    </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
                    {S.xray_opg && (
                        <UploadTile
                            slot={S.xray_opg}
                            state={uploads?.[S.xray_opg.key]}
                            onPick={(f) => onUpload(S.xray_opg.key, f)}
                            onRemove={() => onRemove(S.xray_opg.key)}
                            icon={<Scan size={18} weight="duotone" />}
                            title="OPG panoramic"
                            hint="JPG · PNG · DICOM"
                            required
                        />
                    )}
                    {S.xray_lateral_ceph && (
                        <UploadTile
                            slot={S.xray_lateral_ceph}
                            state={uploads?.[S.xray_lateral_ceph.key]}
                            onPick={(f) => onUpload(S.xray_lateral_ceph.key, f)}
                            onRemove={() => onRemove(S.xray_lateral_ceph.key)}
                            icon={<Scan size={18} weight="regular" />}
                            title="Lateral ceph"
                            hint="For skeletal analysis"
                        />
                    )}
                    {S.cbct && (
                        <UploadTile
                            slot={S.cbct}
                            state={uploads?.[S.cbct.key]}
                            onPick={(f) => onUpload(S.cbct.key, f)}
                            onRemove={() => onRemove(S.cbct.key)}
                            icon={<Stack size={18} weight="duotone" />}
                            title="CBCT"
                            hint="DICOM · ZIP · if available"
                        />
                    )}
                </div>
                <div className="flex items-start gap-2 mt-3 p-2.5 rounded-[8px] bg-indigo-50/60 border-[0.5px] border-indigo-300" data-testid="cbct-recommend-note">
                    <Info size={14} weight="fill" className="text-indigo-700 shrink-0 mt-0.5" />
                    <p className="text-[11.5px] text-indigo-800 leading-snug">
                        CBCT is optional but strongly recommended for complex cases, severe crowding, or impacted teeth. Upload DICOM files or a ZIP of the full series.
                    </p>
                </div>
            </AccordionCard>

            {/* ── ACCORDION 5 — 3rd-Party Deliverable (Self-Plan only) ── */}
            {isSelfPlan && planTimeline && setPlanTimeline && (
                <AccordionCard
                    testid="acc-deliverable"
                    icon={<Tag size={16} weight="duotone" />}
                    iconTone="purple"
                    title="3rd-Party Deliverable"
                    sub="Planning tool · Viewer link · Optional walkthrough video"
                    count={deliverableCount}
                    total={1}
                    allRequiredDone={deliverableDone}
                    open={!!open.deliverable}
                    onToggle={() => toggle("deliverable")}
                >
                    <p className="text-[11.5px] text-muted-foreground leading-snug mb-3">
                        Declare which professional planning tool authored this plan, then share the live viewer link so the lab and Invisique™ QA can replay your setup.
                    </p>
                    <ThirdPartyDeliverableSection
                        planTimeline={planTimeline}
                        updPlan={(k, v) => setPlanTimeline((p) => ({ ...p, [k]: v }))}
                        caseId={caseId}
                    />
                </AccordionCard>
            )}

            {/* iter98.99t — Confirmation strip ("Before you continue")
                removed per user direction. Consent + scan-quality +
                correct-patient confirmations now live at the final
                Review & Submit step, not here. The clinical flags
                (`scan_quality_confirmed`, `consent_confirmed`,
                `case_correct_confirmed`) are still in the data model
                and writable elsewhere — we just don't ask the doctor
                to tick them mid-upload anymore. */}

            {/* iter98.99u — Upload summary block removed. Each accordion
                header already shows its own X / Y uploaded count + a
                Complete pill once the required tiles are done, so a
                separate roll-up summary at the bottom was redundant. */}
        </div>
    );
}

function ConfirmRow({ checked, onChange, title, sub, last = false, testid }) {
    return (
        <label
            className={`flex items-start gap-2.5 py-2 cursor-pointer ${last ? "" : "border-b-[0.5px] border-border/60"}`}
            data-testid={testid}
        >
            <input
                type="checkbox"
                checked={!!checked}
                onChange={(e) => onChange(e.target.checked)}
                className="w-[15px] h-[15px] mt-0.5 accent-primary cursor-pointer shrink-0"
                data-testid={`${testid}-input`}
            />
            <div>
                <div className="text-[13px] font-medium text-foreground">{title}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>
            </div>
        </label>
    );
}

function SummaryRow({ k, v, last = false }) {
    return (
        <div className={`flex items-baseline justify-between py-1.5 ${last ? "" : "border-b-[0.5px] border-border/40"}`}>
            <span className="text-[11.5px] text-muted-foreground">{k}</span>
            <span className="text-[12px] font-medium text-foreground text-right max-w-[60%] truncate" title={typeof v === "string" ? v : ""}>{v}</span>
        </div>
    );
}

function formatScanDate(dateStr) {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
